import { useEffect, useState } from 'react';
import { Search, Trash2 } from 'lucide-react';

/**
 * Looping, non-interactive marketing demo of the StoryMint dashboard.
 * Mirrors the real Dashboard layout (header, search, book grid) with
 * sample data and one book whose generation progress ticks live.
 * Decorative only — aria-hidden, no interactive controls.
 */

interface MockBook {
  id: string;
  title: string;
  premise: string;
  genre: string;
  wordCount: number;
  chaptersTotal: number;
  status: 'complete' | 'writing';
  /** Set on the one book whose chaptersComplete animates each cycle. */
  animateProgress?: boolean;
  /** Used only for non-animating books. */
  chaptersComplete?: number;
}

const MOCK_BOOKS: MockBook[] = [
  {
    id: '1',
    title: 'The Drawn City',
    premise:
      'A failed cartographer inherits a map that redraws itself each night, leading her into the ruined city beneath her own.',
    genre: 'fantasy',
    wordCount: 51420,
    chaptersTotal: 20,
    chaptersComplete: 20,
    status: 'complete',
  },
  {
    id: '2',
    title: 'Salt and Shore',
    premise:
      'A marine biologist returns to her grandmother’s village and falls for the lighthouse keeper who hides what he has seen in the water.',
    genre: 'romance',
    wordCount: 49870,
    chaptersTotal: 20,
    chaptersComplete: 20,
    status: 'complete',
  },
  {
    id: '3',
    title: 'Quiet After',
    premise:
      'Eight strangers wake in a snowbound cabin with no memory of how they arrived — and one of them is the reason the others are there.',
    genre: 'thriller',
    wordCount: 38240,
    chaptersTotal: 20,
    status: 'writing',
    animateProgress: true,
  },
  {
    id: '4',
    title: 'The Cartographer’s Daughter',
    premise:
      'A girl raised by mapmakers discovers her late mother charted a country that has never officially existed — and is being looked for.',
    genre: 'mystery',
    wordCount: 79410,
    chaptersTotal: 32,
    chaptersComplete: 32,
    status: 'complete',
  },
  {
    id: '5',
    title: 'Tomorrow’s Witness',
    premise:
      'A small-town reporter inherits a typewriter that prints tomorrow’s front page tonight, and quickly learns the cost of changing the headline.',
    genre: 'sci-fi',
    wordCount: 52310,
    chaptersTotal: 20,
    chaptersComplete: 20,
    status: 'complete',
  },
  {
    id: '6',
    title: 'Brass, Glass, Wood',
    premise:
      'A retired clockmaker is hired by a stranger to repair a watch that runs backward and seems to know things it shouldn’t.',
    genre: 'mystery',
    wordCount: 50760,
    chaptersTotal: 20,
    chaptersComplete: 20,
    status: 'complete',
  },
];

function BrowserChrome({ url }: { url: string }) {
  return (
    <div
      className="h-[38px] border-b border-border flex items-center px-4 gap-1.5 shrink-0"
      style={{ background: '#16161f' }}
    >
      <span className="w-3 h-3 rounded-full bg-[#ff5f57] shrink-0" />
      <span className="w-3 h-3 rounded-full bg-[#febc2e] shrink-0" />
      <span className="w-3 h-3 rounded-full bg-[#28c840] shrink-0" />
      <div
        className="flex-1 max-w-[360px] mx-auto h-[22px] rounded flex items-center justify-center text-[11px] text-muted-foreground font-mono px-3"
        style={{ background: 'rgba(255,255,255,0.05)' }}
      >
        {url}
      </div>
    </div>
  );
}

function usePrefersReducedMotion(): boolean {
  const [prefers, setPrefers] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = () => setPrefers(mq.matches);
    handler();
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);
  return prefers;
}

const WRITING_START = 14;
const WRITING_TOTAL = 20;
const TICK_MS = 850;

export function DashboardDemo() {
  const reducedMotion = usePrefersReducedMotion();
  const [writingProgress, setWritingProgress] = useState(WRITING_START);

  useEffect(() => {
    if (reducedMotion) {
      setWritingProgress(WRITING_TOTAL - 1);
      return;
    }
    const id = setInterval(() => {
      setWritingProgress((p) => (p >= WRITING_TOTAL - 1 ? WRITING_START : p + 1));
    }, TICK_MS);
    return () => clearInterval(id);
  }, [reducedMotion]);

  return (
    <div
      role="img"
      aria-label="StoryMint dashboard showing six AI-generated novels with one mid-generation"
      className="rounded-xl overflow-hidden border border-border mb-12"
      style={{ background: '#0d0d12', boxShadow: '0 32px 80px rgba(0,0,0,0.5)' }}
    >
      <BrowserChrome url="app.storymint.io/dashboard" />

      <div
        className="relative w-full overflow-hidden"
        style={{
          height: 'clamp(560px, 64vw, 640px)',
          background: 'hsl(var(--background))',
        }}
        aria-hidden="true"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-6">
          {/* Header row */}
          <div className="flex items-center justify-between mb-4 sm:mb-5">
            <h1 className="text-xl sm:text-2xl font-bold text-foreground">My Books</h1>
            <div className="px-3 sm:px-4 py-1.5 sm:py-2 bg-primary text-primary-foreground rounded-md text-xs sm:text-sm font-medium">
              + New Book
            </div>
          </div>

          {/* Search row */}
          <div className="flex justify-end mb-4 sm:mb-5">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <div className="h-9 w-full rounded-md border border-input bg-background px-3 pl-8 py-1.5 text-sm text-muted-foreground/70 flex items-center">
                Search books…
              </div>
            </div>
          </div>

          {/* Book grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {MOCK_BOOKS.map((book) => {
              const chaptersComplete = book.animateProgress
                ? writingProgress
                : book.chaptersComplete ?? book.chaptersTotal;
              const statusPillClass =
                book.status === 'complete'
                  ? 'bg-green-900/30 text-green-400'
                  : 'bg-blue-900/30 text-blue-400';

              return (
                <div
                  key={book.id}
                  className="relative block p-4 sm:p-5 border border-border rounded-lg bg-card"
                >
                  <div className="flex items-start justify-between mb-2.5 pr-7">
                    <h3 className="font-semibold text-foreground line-clamp-1 text-[15px]">
                      {book.title}
                    </h3>
                    <span
                      className={`ml-2 flex-shrink-0 text-xs px-2 py-0.5 rounded-full transition-colors ${statusPillClass}`}
                    >
                      {book.status}
                    </span>
                  </div>
                  <p className="text-[13px] text-muted-foreground line-clamp-2 mb-3 leading-snug">
                    {book.premise}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="capitalize">{book.genre}</span>
                    <span>·</span>
                    <span>{book.wordCount.toLocaleString()} words</span>
                    <span>·</span>
                    <span
                      className={
                        book.animateProgress ? 'text-blue-400 font-medium' : ''
                      }
                    >
                      {chaptersComplete}/{book.chaptersTotal} chapters
                    </span>
                  </div>

                  <span
                    aria-hidden="true"
                    className="absolute top-3 right-3 p-1 text-muted-foreground"
                  >
                    <Trash2 className="h-4 w-4" />
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
